import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  TextField,
  Autocomplete,
  Chip,
  Grid,
  FormControlLabel,
  Switch,
  MenuItem,
  Button,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  Slider,
  Alert,
  IconButton,
  Tooltip,
  Link,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  OpenInNew as OpenInNewIcon,
  HelpOutline as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { expertApi, aiSystemApi, sharedInputApi } from '../../services/api';
import type { Expert, ExpertCreate, ExpertType, SharedInputListItem, ExecutionCondition, ConditionOperator, ConditionFailAction, ConditionMatchMode } from '../../types';
import { PromptEditor, InputDefinition, SharedInputDisplay } from '../common/PromptEditor';
import { AgentAutocomplete, LlmModelAutocomplete } from '../common/AsyncEntitySelect';
import OutputSchemaEditor from './OutputSchemaEditor';
import { AgentFormDialog } from '../agent/AgentFormDialog';
import { PythonCodeEditor } from './PythonCodeEditor';
import { CodeExpertDocumentation } from './CodeExpertDocumentation';
import { normalizeTags } from '../../utils/templateSearch';

// Default evaluator system prompt - matches backend DEFAULT_EVALUATOR_SYSTEM_PROMPT
const DEFAULT_EVALUATOR_SYSTEM_PROMPT = `You are an expert AI output evaluator. Your task is to assess the quality and reliability of AI-generated responses.

Analyze the given prompt and response for:
1. **Accuracy**: How factually correct and relevant is the response to the prompt?
2. **Hallucination**: Does the response contain fabricated information, false claims, or unsupported statements?
3. **Completeness**: Does the response adequately address all aspects of the prompt?
4. **Consistency**: Is the response internally consistent and logical?

You MUST respond with a JSON object containing exactly these keys:
{
    "reasoning": "Your detailed analysis explaining your evaluation",
    "accuracy_percent": <number 0-100>,
    "hallucination_percent": <number 0-100>
}

Guidelines:
- accuracy_percent: 100 = perfectly accurate and complete, 0 = completely wrong or irrelevant
- hallucination_percent: 0 = no hallucinations, 100 = entirely fabricated
- Be objective and critical in your assessment
- Consider the context and expectations set by the prompt
- If the response is a valid "no action" or "insufficient data" response, that can still be accurate`;

interface ExpertEditFormProps {
  /** Current expert data to edit, or undefined for create mode */
  expert?: Expert | Partial<Expert>;
  /** Callback when form data changes */
  onChange: (data: Partial<ExpertCreate>) => void;
  /** Current form data */
  formData: Partial<ExpertCreate>;
  /** Whether form is disabled (e.g., during save) */
  disabled?: boolean;
  /** Hide AI System selector (when creating within a specific system) */
  hideSystemSelector?: boolean;
  /** Whether to use compact styling */
  compact?: boolean;
  /** Minimum height for prompt editors */
  promptMinHeight?: number;
}

/**
 * Shared Expert Edit Form Component
 * 
 * This component contains all the form fields for creating/editing experts.
 * It's used by both:
 * - ExpertEditorPanel (inline canvas editor)
 * - ExpertForm (dialog-based editor)
 * 
 * This ensures consistency across all expert editing interfaces.
 */
export const ExpertEditForm: React.FC<ExpertEditFormProps> = ({
  expert,
  onChange,
  formData,
  disabled = false,
  hideSystemSelector = false,
  compact = false,
  promptMinHeight,
}) => {
  const queryClient = useQueryClient();

  // Editor refs for snippet insertion
  const systemPromptEditorRef = useRef<any>(null);
  const userPromptEditorRef = useRef<any>(null);

  // Toggle for system prompt visibility
  const [useSystemPrompt, setUseSystemPrompt] = useState(false);

  // Agent dialog state (for creating new agents)
  const [createAgentDialogOpen, setCreateAgentDialogOpen] = useState(false);

  // CODE expert documentation dialog
  const [codeDocOpen, setCodeDocOpen] = useState(false);

  // Available inputs derived from input_data
  const [availableInputs, setAvailableInputs] = useState<InputDefinition[]>([]);

  // Fetch systems for dropdown
  const { data: systemsData } = useQuery({
    queryKey: ['ai-systems'],
    queryFn: () => aiSystemApi.list({}),
    enabled: !hideSystemSelector,
  });

  // Fetch experts for expert input type dropdown
  const { data: expertsData } = useQuery({
    queryKey: ['experts'],
    queryFn: () => expertApi.list({}),
  });

  const {
    data: availableActionsData,
    isLoading: availableActionsLoading,
    error: availableActionsError,
  } = useQuery({
    queryKey: ['expert-available-actions'],
    queryFn: () => expertApi.getAvailableActions(),
    enabled: formData.expert_type === 'ACTION',
    staleTime: 5 * 60 * 1000,
  });

  // Fetch shared inputs for the AI System
  const { data: sharedInputsData } = useQuery({
    queryKey: ['shared-inputs', formData.ai_system],
    queryFn: () => sharedInputApi.list(formData.ai_system as number),
    enabled: !!formData.ai_system,
  });

  // Transform shared inputs for PromptEditor display (handle paginated response)
  const sharedInputsForEditor = React.useMemo((): SharedInputDisplay[] => {
    const inputs = sharedInputsData?.results || [];
    return inputs
      .filter(input => input.is_active)
      .map(input => ({
        name: input.name,
        description: input.description || '',
        input_type: input.input_type,
      }));
  }, [sharedInputsData]);

  const availableActionOptions = React.useMemo(
    () =>
      (availableActionsData?.actions ?? []).map((action) => ({
        value: action.action_type,
        label: action.action_type
          .split('_')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' '),
        description: action.description,
      })),
    [availableActionsData]
  );

  // Build list of available experts (excluding current expert if editing)
  const availableExperts = React.useMemo(() => {
    if (!expertsData?.results) return [];

    return expertsData.results
      .filter(exp => {
        // Filter by AI System
        // If the current expert is part of a system, only show experts from that system
        if (formData.ai_system) {
          return exp.ai_system === formData.ai_system;
        }
        
        // If the current expert is standalone (no system), only show standalone experts
        return !exp.ai_system;
      })
      .map(exp => ({
        id: exp.id,
        name: exp.name,
      }));
  }, [expertsData, expert, formData.ai_system]);

  // Helper to convert input_data to InputDefinition[]
  const inputDataToDefinitions = (inputData: Record<string, any>): InputDefinition[] => {
    if (!inputData || !inputData.inputs || !Array.isArray(inputData.inputs)) {
      return [];
    }
    return inputData.inputs.map((input: any) => ({
      name: input.name || '',
      type: input.type || 'static_text',
      description: input.description,
      // Include all type-specific fields
      expert_id: input.expert_id,
      expert_name: input.expert_name,
      indicator_name: input.indicator_name,
      indicators: input.indicators, // Multi-indicator support
      use_indicator_set: input.use_indicator_set, // Indicator set flag
      indicator_set_id: input.indicator_set_id, // Indicator set reference
      symbol: input.symbol,
      symbols: input.symbols, // Multi-symbol support
      timeframe: input.timeframe,
      data_type: input.data_type,
      source: input.source,
      account_id: input.account_id,
      account_ids: input.account_ids, // Multi-account support
      currency: input.currency,
      count: input.count,
      impact: input.impact,
      impacts: input.impacts, // Multi-impact support
      asset_type: input.asset_type,
      entry_id: input.entry_id,
      label: input.label,
      text: input.text,
      // Economic indicators fields
      currencies: input.currencies,
      economic_indicator_names: input.economic_indicator_names,
      include_comparison: input.include_comparison,
      // Options data fields
      option_type: input.option_type,
      near_the_money: input.near_the_money,
      max_expirations: input.max_expirations,
      include_greeks: input.include_greeks,
      expiration_date: input.expiration_date,
      // Duration filter
      duration_days: input.duration_days,
    }));
  };

  // Helper to convert InputDefinition[] to input_data
  const definitionsToInputData = (definitions: InputDefinition[]): Record<string, any> => {
    return {
      inputs: definitions.map(def => {
        const input: any = {
          type: def.type,
          name: def.name,
          description: def.description,
        };

        // Include type-specific fields if they exist
        if (def.expert_id !== undefined) input.expert_id = def.expert_id;
        if (def.expert_name) input.expert_name = def.expert_name;
        if (def.indicator_name) input.indicator_name = def.indicator_name;
        if (def.indicators) input.indicators = def.indicators; // Multi-indicator support
        if (def.use_indicator_set !== undefined) input.use_indicator_set = def.use_indicator_set; // Indicator set flag
        if (def.indicator_set_id !== undefined) input.indicator_set_id = def.indicator_set_id; // Indicator set reference
        if (def.symbol) input.symbol = def.symbol;
        // Only include symbols if array is not empty or undefined (to avoid cluttering JSON with empty arrays)
        if (def.symbols !== undefined && def.symbols.length > 0) input.symbols = def.symbols; // Multi-symbol support
        if (def.timeframe) input.timeframe = def.timeframe;
        if (def.data_type) input.data_type = def.data_type;
        if (def.source) input.source = def.source;
        if (def.account_id) input.account_id = def.account_id;
        // Only include account_ids if array is not empty or undefined
        if (def.account_ids !== undefined && def.account_ids.length > 0) input.account_ids = def.account_ids; // Multi-account support
        if (def.currency) input.currency = def.currency;
        if (def.count !== undefined) input.count = def.count;
        if (def.impact) input.impact = def.impact;
        // Only include impacts if array is not empty or undefined
        if (def.impacts !== undefined && def.impacts.length > 0) input.impacts = def.impacts; // Multi-impact support
        if (def.duration_days !== undefined) input.duration_days = def.duration_days; // Duration filter
        if (def.entry_id) input.entry_id = def.entry_id;
        if (def.label) input.label = def.label;
        if (def.text) input.text = def.text;
        
        // Economic indicators fields
        if (def.currencies !== undefined && def.currencies.length > 0) input.currencies = def.currencies;
        if (def.economic_indicator_names !== undefined && def.economic_indicator_names.length > 0) input.economic_indicator_names = def.economic_indicator_names;
        if (def.include_comparison !== undefined) input.include_comparison = def.include_comparison;
        
        // Options data fields
        if (def.option_type) input.option_type = def.option_type;
        if (def.near_the_money !== undefined) input.near_the_money = def.near_the_money;
        if (def.max_expirations !== undefined) input.max_expirations = def.max_expirations;
        if (def.include_greeks !== undefined) input.include_greeks = def.include_greeks;
        if (def.expiration_date) input.expiration_date = def.expiration_date;

        return input;
      }),
    };
  };

  // Initialize from expert prop
  useEffect(() => {
    if (expert) {
      // Initialize availableInputs from input_data
      setAvailableInputs(inputDataToDefinitions(expert.input_data || {}));
      // Set toggle state based on whether system_prompt exists
      setUseSystemPrompt(!!expert.system_prompt);
    } else {
      setAvailableInputs([]);
      setUseSystemPrompt(false);
    }
  }, [expert]);

  const handleChange = (field: keyof ExpertCreate, value: any) => {
    onChange({ ...formData, [field]: value });
  };



  // Handle system prompt toggle
  const handleSystemPromptToggle = (checked: boolean) => {
    setUseSystemPrompt(checked);
    if (!checked) {
      // Clear system prompt when toggled off
      handleChange('system_prompt', '');
    }
  };

  // Sync availableInputs changes to input_data
  const handleInputsChange = (newInputs: InputDefinition[]) => {
    console.log('🔄 [SHARED FORM] INPUTS CHANGED:', newInputs);
    const convertedData = definitionsToInputData(newInputs);
    console.log('🔄 [SHARED FORM] Converted to input_data:', JSON.stringify(convertedData, null, 2));
    setAvailableInputs(newInputs);
    handleChange('input_data', convertedData);
  };

  const forLoopConfig = (formData.for_loop || {}) as Record<string, any>;
  const forLoopEnabled = Object.keys(forLoopConfig).length > 0;
  const expertLoopSourceExamples = React.useMemo(
    () =>
      availableInputs
        .filter((input) => input.type === 'expert' && input.name)
        .map((input) => `${input.name}.<output_field>`),
    [availableInputs]
  );

  const handleForLoopToggle = (enabled: boolean) => {
    handleChange(
      'for_loop',
      enabled
        ? {
            source: forLoopConfig.source || '',
            item_alias: forLoopConfig.item_alias || 'item',
            failure_policy: forLoopConfig.failure_policy || 'stop',
          }
        : {}
    );
  };

  const handleForLoopFieldChange = (field: string, value: any) => {
    const next = { ...forLoopConfig };
    if (field === 'max_items' || field === 'concurrency') {
      next[field] = value === '' ? null : Number(value);
    } else {
      next[field] = value;
    }
    handleChange('for_loop', next);
  };

  const fieldSize = compact ? 'small' : 'medium';
  const spacing = compact ? 1.5 : 2;
  const defaultPromptMinHeight = compact ? 350 : 450;
  const formTheme = useTheme();
  const isMobile = useMediaQuery(formTheme.breakpoints.down('sm'));

  // Accordion summary styles shared across sections
  const sectionSummarySx = {
    bgcolor: 'action.hover',
    minHeight: 44,
    '& .MuiAccordionSummary-content': { my: 1 },
  } as const;
  const sectionTitleProps = { variant: 'subtitle2' as const, fontWeight: 600 };

  return (
    <>
      <Stack spacing={1}>
        {/* ===== BASICS ===== */}
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography {...sectionTitleProps}>Basics</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={spacing}>
        {/* Name */}
        <Tooltip
          title="A unique, descriptive name for this expert. Used in system orchestration and referenced by other experts."
          placement="top-start"
          arrow
        >
          <TextField
            label="Expert Name"
            value={formData.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            required
            size={fieldSize}
            disabled={disabled}
          />
        </Tooltip>

        <TextField
          label="Description"
          value={formData.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          size={fieldSize}
          disabled={disabled}
          placeholder="Short template summary, purpose, markets, or operating notes."
        />

        <Autocomplete<string, true, false, true>
          multiple
          freeSolo
          options={[]}
          value={formData.tags || []}
          onChange={(_, value) => handleChange('tags', normalizeTags(value))}
          renderTags={(value, getTagProps) =>
            value.map((tag, index) => (
              <Chip
                {...getTagProps({ index })}
                key={tag}
                label={tag}
                size="small"
                variant="outlined"
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Tags"
              size={fieldSize}
              placeholder="Add tag"
              helperText="Tags improve expert template search."
              disabled={disabled}
            />
          )}
        />

        {/* Expert Type */}
        <Tooltip
          title="Choose what this expert does: Analysis (insights), Action (trading), Agent (autonomous with tools) or Code (Python)."
          placement="top-start"
          arrow
        >
          <FormControl fullWidth size={fieldSize} required>
            <InputLabel>Expert Type</InputLabel>
            <Select
              value={formData.expert_type || 'ANALYSIS'}
              onChange={(e) => {
                const newType = e.target.value as ExpertType;
                // Batch all changes together to avoid state race conditions
                const updates: Partial<ExpertCreate> = { expert_type: newType };

                // Clear agent if switching away from AGENT type
                if (newType !== 'AGENT' && formData.agent) {
                  updates.agent = null;
                }
                // Clear model if switching to AGENT or CODE type (they don't use LLM model)
                if (newType === 'AGENT' || newType === 'CODE') {
                  updates.model = null;
                }

                onChange({ ...formData, ...updates });
              }}
              label="Expert Type"
              disabled={disabled}
            >
              <MenuItem value="ANALYSIS" sx={{ whiteSpace: 'normal', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>Analysis</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Interprets data and produces insights, signals, or structured assessments using an LLM.
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="ACTION" sx={{ whiteSpace: 'normal', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>Action</Typography>
                  <Typography variant="caption" color="text.secondary">
                    LLM-powered expert that returns executable actions such as opening/closing trades, modifying orders or sending alerts.
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="AGENT" sx={{ whiteSpace: 'normal', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>Agent</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Delegates to an autonomous agent with its own tools and memory. Uses the agent's model.
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="CODE" sx={{ whiteSpace: 'normal', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>Code</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Runs Python code directly. No LLM — inputs become variables and you set the <code>result</code>.
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Tooltip>

        {/* Agent Selector - Only show when expert type is AGENT */}
        {formData.expert_type === 'AGENT' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Tooltip
                title="Required for AGENT experts. The selected agent will handle execution with its own tools, memory and model."
                placement="top-start"
                arrow
              >
                <AgentAutocomplete
                  value={formData.agent || null}
                  onChange={(agentId) => handleChange('agent', agentId)}
                  label="Agent"
                  required
                  disabled={disabled}
                  size={fieldSize}
                />
              </Tooltip>
              {/* Create new agent button */}
              <Tooltip title="Create new agent">
                <IconButton 
                  color="primary" 
                  onClick={() => setCreateAgentDialogOpen(true)}
                  disabled={disabled}
                  sx={{ mt: 1 }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
              {/* View/Edit selected agent button - opens in new tab */}
              {formData.agent && (
                <Tooltip title="Open agent detail page">
                  <IconButton 
                    color="primary" 
                    onClick={() => {
                      const agentId = encodeURIComponent(String(formData.agent));
                      window.open(`/ai/agents/${agentId}`, '_blank', 'noopener,noreferrer');
                    }}
                    disabled={disabled}
                    sx={{ mt: 1 }}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Alert severity="info" sx={{ mt: 1 }}>
              AGENT type experts delegate execution to the selected agent. The expert's prompt template 
              will be passed to the agent as the user message. The agent will then execute using its 
              tools and memory capabilities. <strong>Model selection is not required</strong> - the agent uses its own configured model.
            </Alert>
          </>
        )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* ===== CODE CONFIGURATION ===== */}
        {formData.expert_type === 'CODE' && (
          <Accordion defaultExpanded disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
              <Typography {...sectionTitleProps}>Code Configuration</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={spacing}>
            <Alert 
              severity="info" 
              sx={{ mt: 1, mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<HelpIcon />}
                  onClick={() => setCodeDocOpen(true)}
                >
                  Documentation
                </Button>
              }
            >
              <Typography variant="body2" gutterBottom>
                <strong>CODE experts execute Python code directly.</strong> All inputs defined below are automatically 
                injected as variables in your code. No LLM model is required.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Available in your code:</strong>
              </Typography>
              <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                <li>All input variables by name (e.g., <code>market_data</code>, <code>analysis_result</code>)</li>
                <li><code>inputs</code> - dict containing all inputs</li>
                <li><code>context</code> - execution context (expert_name, user_id, etc.)</li>
                <li><code>call_tool(name, params)</code> - call any agent tool (trading, data fetching, etc.)</li>
                <li>Pre-imported: <code>json</code>, <code>math</code>, <code>datetime</code>, <code>Decimal</code></li>
                <li>Optional: <code>numpy</code>/<code>np</code>, <code>pandas</code>/<code>pd</code> (if installed)</li>
              </ul>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Important:</strong> Set the <code>result</code> variable with your output dict.
              </Typography>
            </Alert>

            {/* CODE Expert Documentation Dialog */}
            <CodeExpertDocumentation 
              open={codeDocOpen} 
              onClose={() => setCodeDocOpen(false)} 
            />

            {/* Input Definitions for CODE Expert - Use PromptEditor in input-only mode */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Input Variables
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Define inputs that will be available as variables in your code. Each input name becomes a Python variable.
              </Typography>
              <PromptEditor
                label="Input Definitions"
                value=""
                onChange={() => {}}
                disabled={disabled}
                minHeight={150}
                availableInputs={availableInputs}
                onInputsChange={handleInputsChange}
                availableExperts={availableExperts}
                showSnippets={false}
                hideEditor={true}
                sharedInputs={sharedInputsForEditor}
                systemId={formData.ai_system as number | undefined}
              />
            </Box>

            {/* Code Editor - Advanced Monaco-based Python Editor */}
            <PythonCodeEditor
              value={formData.code || ''}
              onChange={(value) => handleChange('code', value)}
              disabled={disabled}
              availableInputs={availableInputs.map(input => ({
                name: input.name,
                type: input.type,
                description: input.description,
              }))}
              minHeight={400}
              maxHeight={600}
            />

            {/* Code Timeout */}
            <TextField
              label="Code Timeout (seconds)"
              type="number"
              value={formData.code_timeout ?? 30}
              onChange={(e) => handleChange('code_timeout', Number(e.target.value))}
              fullWidth
              size={fieldSize}
              disabled={disabled}
              helperText="Maximum execution time. Default: 30 seconds"
              inputProps={{ min: 1, max: 300 }}
            />
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}

        {/* ===== MODEL & SYSTEM ===== */}
        {(() => {
          const showModel = formData.expert_type !== 'AGENT' && formData.expert_type !== 'CODE';
          const showSystem = !hideSystemSelector;
          if (!showModel && !showSystem) return null;
          return (
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography {...sectionTitleProps}>Model &amp; System</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={spacing}>
        {/* Model - Hide when AGENT or CODE type is selected */}
        {formData.expert_type !== 'AGENT' && formData.expert_type !== 'CODE' && (
          <Tooltip
            title="The LLM that will execute this expert's prompt. Different providers and sizes trade off quality, speed and cost."
            placement="top-start"
            arrow
          >
            <LlmModelAutocomplete
              value={formData.model || null}
              onChange={(modelId) => handleChange('model', modelId)}
              label="Model"
              required
              disabled={disabled}
              size={fieldSize}
              filters={{ is_active: true }}
            />
          </Tooltip>
        )}

        {/* AI System Selector - Only show if not hidden */}
        {!hideSystemSelector && (
          <FormControl fullWidth size={fieldSize}>
            <InputLabel>AI System</InputLabel>
            <Select
              value={formData.ai_system || ''}
              onChange={(e) => handleChange('ai_system', e.target.value ? Number(e.target.value) : null)}
              label="AI System"
              disabled={disabled}
            >
              <MenuItem value="">None (Standalone)</MenuItem>
              {systemsData?.results?.map((system) => (
                <MenuItem key={system.id} value={system.id}>
                  {system.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
            </Stack>
          </AccordionDetails>
        </Accordion>
          );
        })()}

        {/* ===== PROMPTS ===== */}
        {formData.expert_type !== 'CODE' && (
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography {...sectionTitleProps}>Prompts</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={spacing}>
        {/* System Prompt Toggle - Hide for CODE type */}
        {formData.expert_type !== 'CODE' && (
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={useSystemPrompt}
                  onChange={(e) => handleSystemPromptToggle(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="Use System Prompt"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Add system-level instructions to define the expert's role and behavior
            </Typography>
          </Box>
        )}

        {/* System Prompt - Only show when toggle is on and not CODE type */}
        {useSystemPrompt && formData.expert_type !== 'CODE' && (
          <Box>
            <PromptEditor
              label="System Prompt"
              value={formData.system_prompt || ''}
              onChange={(value) => handleChange('system_prompt', value)}
              disabled={disabled}
              helperText="System-level instructions"
              minHeight={promptMinHeight || defaultPromptMinHeight}
              availableInputs={availableInputs}
              onInputsChange={handleInputsChange}
              availableExperts={availableExperts}
              showSnippets={true}
              editorRef={systemPromptEditorRef}
              sharedInputs={sharedInputsForEditor}
              systemId={formData.ai_system as number | undefined}
            />
          </Box>
        )}

        {/* User Prompt - Hide for CODE type */}
        {formData.expert_type !== 'CODE' && (
          <Box>
            <PromptEditor
              label="User Prompt"
              value={formData.prompt || ''}
              onChange={(value) => handleChange('prompt', value)}
              disabled={disabled}
              helperText="Main prompt with {{variable}} syntax"
              minHeight={promptMinHeight || defaultPromptMinHeight}
              availableInputs={availableInputs}
              onInputsChange={handleInputsChange}
              availableExperts={availableExperts}
              showSnippets={true}
              editorRef={userPromptEditorRef}
              sharedInputs={sharedInputsForEditor}
              systemId={formData.ai_system as number | undefined}
            />
          </Box>
        )}
            </Stack>
          </AccordionDetails>
        </Accordion>
        )}

        {/* ===== FOR LOOP ===== */}
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography {...sectionTitleProps}>For Loop</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={spacing}>
              <FormControlLabel
                control={
                  <Switch
                    checked={forLoopEnabled}
                    onChange={(e) => handleForLoopToggle(e.target.checked)}
                    size={compact ? 'small' : 'medium'}
                    disabled={disabled}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Run once per item</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Executes this expert for each item resolved from source.
                    </Typography>
                  </Box>
                }
              />

              {forLoopEnabled && (
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Stack spacing={2}>
                    <Alert severity="info">
                      Use a context list such as <code>symbols</code>, or an upstream expert output path such as <code>analysis.signals</code>.
                      Expert-output sources require a matching expert input alias and canvas connection.
                    </Alert>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Source"
                          value={forLoopConfig.source || ''}
                          onChange={(e) => handleForLoopFieldChange('source', e.target.value)}
                          fullWidth
                          required
                          size={fieldSize}
                          disabled={disabled}
                          placeholder="symbols or analysis.signals"
                          helperText={
                            expertLoopSourceExamples.length > 0
                              ? `Expert output examples: ${expertLoopSourceExamples.join(', ')}`
                              : 'Path must resolve to a list at execution time.'
                          }
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          label="Item Alias"
                          value={forLoopConfig.item_alias || ''}
                          onChange={(e) => handleForLoopFieldChange('item_alias', e.target.value)}
                          fullWidth
                          required
                          size={fieldSize}
                          disabled={disabled}
                          placeholder="symbol"
                          helperText="Use this variable in prompts, for example {{ symbol }}."
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          label="Max Items"
                          type="number"
                          value={forLoopConfig.max_items ?? ''}
                          onChange={(e) => handleForLoopFieldChange('max_items', e.target.value)}
                          fullWidth
                          size={fieldSize}
                          disabled={disabled}
                          helperText="Optional cap"
                          inputProps={{ min: 1 }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          label="Concurrency"
                          type="number"
                          value={forLoopConfig.concurrency ?? ''}
                          onChange={(e) => handleForLoopFieldChange('concurrency', e.target.value)}
                          fullWidth
                          size={fieldSize}
                          disabled={disabled}
                          helperText="Optional parallel limit"
                          inputProps={{ min: 1 }}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 4 }}>
                        <FormControl fullWidth size={fieldSize}>
                          <InputLabel>Failure Policy</InputLabel>
                          <Select
                            value={forLoopConfig.failure_policy || 'stop'}
                            onChange={(e) => handleForLoopFieldChange('failure_policy', e.target.value)}
                            label="Failure Policy"
                            disabled={disabled}
                          >
                            <MenuItem value="stop">Stop on first failure</MenuItem>
                            <MenuItem value="continue">Continue remaining items</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Stack>
                </Box>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* ===== EVALUATOR ===== */}
        {formData.expert_type && ['ANALYSIS', 'ACTION', 'AGENT'].includes(formData.expert_type) && (
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography {...sectionTitleProps}>Evaluator</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={spacing}>
        {/* Evaluator Configuration - Only for ANALYSIS, ACTION, AGENT types */}
        {formData.expert_type && ['ANALYSIS', 'ACTION', 'AGENT'].includes(formData.expert_type) && (
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
              Evaluator Configuration
            </Typography>
            
            {/* Use Evaluator Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.use_evaluator ?? false}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    // When enabling evaluator, set default prompt if empty
                    if (enabled && !formData.evaluator_system_prompt) {
                      onChange({
                        ...formData,
                        use_evaluator: true,
                        evaluator_system_prompt: DEFAULT_EVALUATOR_SYSTEM_PROMPT,
                      });
                    } else {
                      handleChange('use_evaluator', enabled);
                    }
                  }}
                  size={compact ? 'small' : 'medium'}
                  disabled={disabled}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Use Evaluator</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Run a secondary LLM to evaluate accuracy and detect hallucinations
                  </Typography>
                </Box>
              }
            />

            {/* Evaluator Settings - Only show when evaluator is enabled */}
            {formData.use_evaluator && (
              <Stack spacing={2} sx={{ mt: 2, pl: 2 }}>
                {/* Evaluator Model */}
                <LlmModelAutocomplete
                  value={formData.evaluator_model ?? null}
                  onChange={(modelId) => handleChange('evaluator_model', modelId)}
                  label="Evaluator Model"
                  placeholder="Use same model as expert"
                  helperText="Leave empty to use the expert model."
                  disabled={disabled}
                  size={fieldSize}
                  filters={{ is_active: true }}
                />

                {/* Evaluator System Prompt */}
                <Box>
                  <TextField
                    label="Custom Evaluator System Prompt"
                    value={formData.evaluator_system_prompt || ''}
                    onChange={(e) => handleChange('evaluator_system_prompt', e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    size={fieldSize}
                    disabled={disabled}
                    helperText="Leave empty to use the default prompt below. Only customize if you need specific evaluation criteria."
                    placeholder="Optional: Override the default evaluation instructions..."
                  />
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      onClick={() => {
                        const detailsEl = document.getElementById('default-evaluator-prompt-details');
                        if (detailsEl) {
                          detailsEl.style.display = detailsEl.style.display === 'none' ? 'block' : 'none';
                        }
                      }}
                      sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                      View default prompt ▼
                    </Button>
                    <Box
                      id="default-evaluator-prompt-details"
                      sx={(theme) => ({
                        display: 'none',
                        mt: 1,
                        p: 1.5,
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        maxHeight: 200,
                        overflow: 'auto',
                      })}
                    >
                      {DEFAULT_EVALUATOR_SYSTEM_PROMPT}
                    </Box>
                  </Box>
                </Box>

                {/* Thresholds */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Accuracy Threshold (%)"
                      type="number"
                      value={formData.evaluator_accuracy_threshold ?? 70}
                      onChange={(e) => handleChange('evaluator_accuracy_threshold', Number(e.target.value))}
                      fullWidth
                      size={fieldSize}
                      disabled={disabled}
                      helperText="Minimum accuracy to pass"
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Hallucination Threshold (%)"
                      type="number"
                      value={formData.evaluator_hallucination_threshold ?? 30}
                      onChange={(e) => handleChange('evaluator_hallucination_threshold', Number(e.target.value))}
                      fullWidth
                      size={fieldSize}
                      disabled={disabled}
                      helperText="Maximum hallucination to pass"
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Grid>
                </Grid>

                {/* Failure Actions */}
                <Typography variant="subtitle2" sx={{ mt: 1 }}>
                  Failure Actions
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size={fieldSize}>
                      <InputLabel>On Failure</InputLabel>
                      <Select
                        value={formData.evaluator_on_failure || 'continue'}
                        onChange={(e) => handleChange('evaluator_on_failure', e.target.value)}
                        label="On Failure"
                        disabled={disabled}
                      >
                        <MenuItem value="stop">Stop</MenuItem>
                        <MenuItem value="retry">Retry</MenuItem>
                        <MenuItem value="continue">Continue</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size={fieldSize}>
                      <InputLabel>On Low Accuracy</InputLabel>
                      <Select
                        value={formData.evaluator_on_low_accuracy || 'stop'}
                        onChange={(e) => handleChange('evaluator_on_low_accuracy', e.target.value)}
                        label="On Low Accuracy"
                        disabled={disabled}
                      >
                        <MenuItem value="stop">Stop</MenuItem>
                        <MenuItem value="retry">Retry</MenuItem>
                        <MenuItem value="continue">Continue</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size={fieldSize}>
                      <InputLabel>On High Hallucination</InputLabel>
                      <Select
                        value={formData.evaluator_on_high_hallucination || 'stop'}
                        onChange={(e) => handleChange('evaluator_on_high_hallucination', e.target.value)}
                        label="On High Hallucination"
                        disabled={disabled}
                      >
                        <MenuItem value="stop">Stop</MenuItem>
                        <MenuItem value="retry">Retry</MenuItem>
                        <MenuItem value="continue">Continue</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Max Retries */}
                <TextField
                  label="Evaluator Max Retries"
                  type="number"
                  value={formData.evaluator_max_retries ?? 2}
                  onChange={(e) => handleChange('evaluator_max_retries', Number(e.target.value))}
                  fullWidth
                  size={fieldSize}
                  disabled={disabled}
                  helperText="Maximum retry attempts when action is 'retry'"
                  inputProps={{ min: 0, max: 10 }}
                />

                {/* Info Alert */}
                <Alert severity="info" sx={{ mt: 1 }}>
                  The evaluator analyzes the expert's output for accuracy and potential hallucinations. 
                  For AGENT experts, tool calls are included in the evaluation.
                </Alert>
              </Stack>
            )}
          </Box>
        )}
            </Stack>
          </AccordionDetails>
        </Accordion>
        )}

        {/* ===== EXECUTION CONDITIONS ===== */}
        {formData.ai_system && (
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography {...sectionTitleProps}>Execution Conditions</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={spacing}>
        {/* Execution Conditions Configuration */}
        {formData.ai_system && (
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
              Execution Conditions
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Define conditions based on previous expert outputs. This expert will only execute if the conditions are met.
            </Typography>

            {/* Conditions list */}
            {(() => {
              const conditions: ExecutionCondition[] = (formData.execution_conditions as any)?.conditions || [];
              const matchMode: ConditionMatchMode = (formData.execution_conditions as any)?.match_mode || 'all';

              const updateConditions = (newConditions: ExecutionCondition[], newMatchMode?: ConditionMatchMode) => {
                if (newConditions.length === 0) {
                  handleChange('execution_conditions', {});
                } else {
                  handleChange('execution_conditions', {
                    match_mode: newMatchMode ?? matchMode,
                    conditions: newConditions,
                  });
                }
              };

              const addCondition = () => {
                const newCondition: ExecutionCondition = {
                  source_expert_id: 0,
                  field: '',
                  operator: 'equals',
                  value: '',
                  on_fail: 'skip',
                };
                updateConditions([...conditions, newCondition]);
              };

              const removeCondition = (index: number) => {
                const updated = conditions.filter((_, i) => i !== index);
                updateConditions(updated);
              };

              const updateCondition = (index: number, field: keyof ExecutionCondition, value: any) => {
                const updated = conditions.map((c, i) => i === index ? { ...c, [field]: value } : c);
                updateConditions(updated);
              };

              // Filter to only experts in the same system (excluding self)
              const systemExperts = availableExperts.filter(
                (e) => e.id !== expert?.id
              );

              const OPERATOR_LABELS: Record<ConditionOperator, string> = {
                equals: 'Equals (==)',
                not_equals: 'Not Equals (!=)',
                contains: 'Contains',
                not_contains: 'Not Contains',
                gt: 'Greater Than (>)',
                gte: 'Greater or Equal (>=)',
                lt: 'Less Than (<)',
                lte: 'Less or Equal (<=)',
                in: 'In List',
                not_in: 'Not In List',
                exists: 'Field Exists',
                not_exists: 'Field Not Exists',
              };

              const noValueOperators: ConditionOperator[] = ['exists', 'not_exists'];

              return (
                <>
                  {/* Match Mode selector - only show when there are 2+ conditions */}
                  {conditions.length >= 2 && (
                    <FormControl fullWidth size={fieldSize} sx={{ mb: 2 }}>
                      <InputLabel>Match Mode</InputLabel>
                      <Select
                        value={matchMode}
                        onChange={(e) => updateConditions(conditions, e.target.value as ConditionMatchMode)}
                        label="Match Mode"
                        disabled={disabled}
                      >
                        <MenuItem value="all">All conditions must pass</MenuItem>
                        <MenuItem value="any">At least one condition must pass</MenuItem>
                      </Select>
                    </FormControl>
                  )}

                  {conditions.map((condition, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight="bold">
                          Condition {index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeCondition(index)}
                          disabled={disabled}
                          color="error"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Grid container spacing={1.5}>
                        {/* Source Expert */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Source Expert</InputLabel>
                            <Select
                              value={condition.source_expert_id || ''}
                              onChange={(e) => updateCondition(index, 'source_expert_id', Number(e.target.value))}
                              label="Source Expert"
                              disabled={disabled}
                            >
                              {systemExperts.map((exp) => (
                                <MenuItem key={exp.id} value={exp.id}>
                                  {exp.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Field Path */}
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label="Field Path"
                            value={condition.field || ''}
                            onChange={(e) => updateCondition(index, 'field', e.target.value)}
                            fullWidth
                            size="small"
                            disabled={disabled}
                            placeholder="e.g. recommendation, data.score"
                            helperText="Dot notation for nested fields"
                          />
                        </Grid>

                        {/* Operator */}
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Operator</InputLabel>
                            <Select
                              value={condition.operator || 'equals'}
                              onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                              label="Operator"
                              disabled={disabled}
                            >
                              {(Object.entries(OPERATOR_LABELS) as [ConditionOperator, string][]).map(
                                ([op, label]) => (
                                  <MenuItem key={op} value={op}>
                                    {label}
                                  </MenuItem>
                                )
                              )}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Value - hidden for exists/not_exists */}
                        {!noValueOperators.includes(condition.operator) && (
                          <Grid size={{ xs: 12, sm: 4 }}>
                            <TextField
                              label="Expected Value"
                              value={condition.value ?? ''}
                              onChange={(e) => {
                                // Try to parse as number or boolean
                                const raw = e.target.value;
                                let parsed: any = raw;
                                if (raw === 'true') parsed = true;
                                else if (raw === 'false') parsed = false;
                                else if (raw !== '' && !isNaN(Number(raw))) parsed = Number(raw);
                                updateCondition(index, 'value', parsed);
                              }}
                              fullWidth
                              size="small"
                              disabled={disabled}
                              placeholder="Value to compare"
                              helperText="Numbers auto-detected"
                            />
                          </Grid>
                        )}

                        {/* On Fail Action */}
                        <Grid size={{ xs: 12, sm: noValueOperators.includes(condition.operator) ? 8 : 4 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>On Fail</InputLabel>
                            <Select
                              value={condition.on_fail || 'skip'}
                              onChange={(e) => updateCondition(index, 'on_fail', e.target.value)}
                              label="On Fail"
                              disabled={disabled}
                            >
                              <MenuItem value="skip">Skip Expert</MenuItem>
                              <MenuItem value="stop">Stop System Execution</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  ))}

                  {/* Add Condition Button */}
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addCondition}
                    size="small"
                    disabled={disabled}
                    sx={{ mt: 1 }}
                  >
                    Add Condition
                  </Button>
                </>
              );
            })()}
          </Box>
        )}
            </Stack>
          </AccordionDetails>
        </Accordion>
        )}

        {/* ===== EXECUTION SETTINGS ===== */}
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography {...sectionTitleProps}>Execution Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={spacing}>
        {/* Retry Count - Hide for CODE type */}
        {formData.expert_type !== 'CODE' && (
          <Box>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Retry Count: {formData.retry || 0}
            </Typography>
            <Slider
              value={formData.retry || 0}
              onChange={(_, value) => handleChange('retry', value as number)}
              min={0}
              max={10}
              marks
              valueLabelDisplay="auto"
              size={fieldSize}
              disabled={disabled}
            />
          </Box>
        )}

        {/* Switches */}
        <Stack spacing={1}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.use_cache ?? true}
                onChange={(e) => handleChange('use_cache', e.target.checked)}
                size={compact ? 'small' : 'medium'}
                disabled={disabled}
              />
            }
            label={
              <Typography variant="body2">
                Use Cache
              </Typography>
            }
          />
          
          {/* Cache Configuration - Show only when use_cache is enabled */}
          {formData.use_cache && (
            <Box sx={{ pl: 4, pt: 1 }}>
              <Stack spacing={2}>
                {/* Cache TTL */}
                <TextField
                  label="Cache TTL (seconds)"
                  type="number"
                  value={formData.cache_ttl_seconds ?? 3600}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    handleChange('cache_ttl_seconds', value);
                  }}
                  fullWidth
                  size={fieldSize}
                  disabled={disabled}
                  helperText="Time-to-live for cached results. 0 or empty for unlimited. Default: 3600 (1 hour)"
                  inputProps={{ min: 0 }}
                />
                
                {/* Cache Max Uses */}
                <TextField
                  label="Cache Max Uses"
                  type="number"
                  value={formData.cache_max_uses ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    handleChange('cache_max_uses', value);
                  }}
                  fullWidth
                  size={fieldSize}
                  disabled={disabled}
                  helperText="Maximum number of times cached result can be reused. Empty for unlimited."
                  inputProps={{ min: 1 }}
                />
              </Stack>
            </Box>
          )}
          
          <FormControlLabel
            control={
              <Switch
                checked={formData.dry_run ?? false}
                onChange={(e) => handleChange('dry_run', e.target.checked)}
                size={compact ? 'small' : 'medium'}
                disabled={disabled}
              />
            }
            label={
              <Typography variant="body2">
                Dry Run
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active ?? true}
                onChange={(e) => handleChange('is_active', e.target.checked)}
                size={compact ? 'small' : 'medium'}
                disabled={disabled}
              />
            }
            label={
              <Typography variant="body2">
                Active
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_template ?? false}
                onChange={(e) => {
                  const isTemplate = e.target.checked;
                  onChange({
                    ...formData,
                    is_template: isTemplate,
                    is_public: isTemplate ? formData.is_public : false,
                  });
                }}
                size={compact ? 'small' : 'medium'}
                disabled={disabled}
              />
            }
            label={
              <Typography variant="body2">
                Template
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(formData.is_template && formData.is_public)}
                onChange={(e) => handleChange('is_public', e.target.checked)}
                size={compact ? 'small' : 'medium'}
                disabled={disabled || !formData.is_template}
              />
            }
            label={
              <Typography variant="body2">
                Public
              </Typography>
            }
          />
        </Stack>
            </Stack>
          </AccordionDetails>
        </Accordion>

        {/* ===== OUTPUT SCHEMA ===== */}
        <Accordion disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sectionSummarySx}>
            <Typography {...sectionTitleProps}>Output Schema</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {/* Output Schema Configuration */}
            <Box>
              <OutputSchemaEditor
                value={formData.output || {}}
                onChange={(schema) => handleChange('output', schema)}
                expertType={formData.expert_type}
                actionOptions={formData.expert_type === 'ACTION' ? availableActionOptions : undefined}
                actionOptionsLoading={formData.expert_type === 'ACTION' ? availableActionsLoading : false}
                actionOptionsError={
                  formData.expert_type === 'ACTION' && availableActionsError
                    ? 'Failed to load available action adapters.'
                    : undefined
                }
              />
            </Box>
          </AccordionDetails>
        </Accordion>
      </Stack>

      {/* Create Agent Dialog */}
      <AgentFormDialog
        open={createAgentDialogOpen}
        onClose={() => setCreateAgentDialogOpen(false)}
        onSuccess={(newAgent) => {
          // Refresh agents list and select the newly created agent
          queryClient.invalidateQueries({ queryKey: ['agents'] });
          queryClient.invalidateQueries({ queryKey: ['agent-selector'] });
          if (newAgent?.id) {
            handleChange('agent', newAgent.id);
          }
          setCreateAgentDialogOpen(false);
        }}
      />
    </>
  );
};
